/**
 * event_logs_controller.js — business Event Logs page (Stimulus).
 *
 * Renders the recent-events audit table for the current business and wires the
 * Refresh / Download CSV buttons. One instance per page
 * (data-controller="event-logs"). connect() loads events, so it re-runs on every
 * Turbo navigation to /activity — no head-script double-binding, no inline JS.
 *
 * Backend: application/web/views/activity.py (activity_bp). The log is an
 * append-only audit trail, so this page only reads it.
 *   GET  /activity/api/events          -> list
 *   GET  /activity/api/events/export   -> CSV download
 */

import { Controller } from "@hotwired/stimulus";

    var EVENTS_URL = '/activity/api/events';
    var EXPORT_URL = '/activity/api/events/export?limit=1000';

    function eventTypeColor(type) {
        switch ((type || '').toLowerCase()) {
            case 'created': return 'success';
            case 'updated': return 'warning';
            case 'status_changed': return 'info';
            case 'deleted': return 'danger';
            default: return 'secondary';
        }
    }

    function formatTime(timestamp) {
        var date = new Date(timestamp);
        var pad = function (n) { return String(n).padStart(2, '0'); };
        return pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear() +
            ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds());
    }

    function escapeHtml(text) {
        var div = document.createElement('div');
        div.textContent = text == null ? '' : text;
        return div.innerHTML;
    }

    function notify(message, type) {
        if (typeof window.showNotification === 'function') {
            window.showNotification(message, type);
        } else if (type === 'error') {
            alert('Error: ' + message);
        } else {
            alert(message);
        }
    }

export default class extends Controller {
        connect() {
            this.tbody = this.element.querySelector('#eventsTable');
            this.load();
        }

        load() {
            var self = this;
            fetch(EVENTS_URL, { credentials: 'same-origin' })
                .then(function (response) { return response.json(); })
                .then(function (data) { self._render(data); })
                .catch(function (error) {
                    console.error('Error loading events:', error);
                    self._renderError(error);
                });
        }

        refresh() {
            this.load();
        }

        download() {
            var link = document.createElement('a');
            link.href = EXPORT_URL;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            notify('Event logs download started', 'success');
        }

        _render(data) {
            if (!this.tbody) return;
            if (data.success && data.events.length > 0) {
                this.tbody.innerHTML = data.events.map(function (event) {
                    return '<tr>' +
                        '<td class="text-muted small">' + formatTime(event.created_at) + '</td>' +
                        '<td class="text-muted small">' + escapeHtml(event.user_email || 'System') + '</td>' +
                        '<td><span class="badge bg-' + eventTypeColor(event.event_type) + '">' +
                            escapeHtml(event.event_type) + '</span></td>' +
                        '<td class="text-muted small">' + escapeHtml(event.entity_type) + ' #' + escapeHtml(event.entity_id) + '</td>' +
                        '<td class="text-muted small">' + escapeHtml(event.client_name || 'N/A') + '</td>' +
                        '<td><span class="text-dark">' + escapeHtml(event.description) + '</span></td>' +
                        '</tr>';
                }).join('');
            } else {
                var message = data.info_message || (data.success ? 'No events available' : 'Error loading events');
                var messageClass = data.info_message ? 'text-info' : (data.success ? 'text-muted' : 'text-danger');
                this.tbody.innerHTML = '<tr><td colspan="6" class="text-center ' + messageClass + '">' +
                    escapeHtml(message) + '</td></tr>';
            }
        }

        _renderError(error) {
            if (!this.tbody) return;
            this.tbody.innerHTML = '<tr><td colspan="6" class="text-center text-danger">' +
                '<i class="bi bi-exclamation-triangle"></i> Error loading events' +
                '<br><small class="text-muted">Error: ' + escapeHtml(error.message) + '</small></td></tr>';
        }
}
